import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import { useErrorSnackbar } from "../../../hooks/useErrorSnackbar";
import { useLastNameFirst } from "../../../hooks/useNameFormat";
import { useNurseTimeOff } from "../../../hooks/useNurseTimeOff";
import { useRosterLookup } from "../../../hooks/useRosterLookup";
import type { Job, Nurse, NurseData, WeekData } from "../../../utils/models";
import { createDateRange, generateShiftData } from "../../../utils/nursePanelUtils";
import { buildUnassignedJob } from "../../staffing/MonthlyView/jobView/monthlyJobUtils";
import { useNurseRemoval } from "../hooks/useNurseRemoval";
import { useGetCalledOffShiftsThisWeek } from "../queries";
import {
	buildDemoListedShifts,
	buildRemoveStaffRows,
	collectPlacementShifts,
	countUnplacedAsOther,
	type RemoveStaffRow,
	staffingDateOf,
} from "./removeStaffRows";
import { deriveRemoveStaffWindows, type RemoveStaffWindows } from "./removeStaffWindows";

/** How far back the "Shift removals" column counts a nurse's call-offs — the weeks before this one. */
const CALL_OFF_LOOKBACK_WEEKS = 4;

/** A nurse with no call-off in the lookback: zero, and a "last" that sorts before every real one. */
const NO_CALL_OFFS: NonNullable<NurseData["lastWeekCalloffs"]> = { count: 0, lastCalloffTime: DateTime.fromMillis(0) };

/** What the date's shift list is: every shift on it, as the remove list reports them (the demo reads the loaded day). */
const useListedShifts = (week: WeekData, dayNo: number, staffingDate: DateTime) => {
	const { chosenDepartment } = useChosenDepartment();
	const { isHospitalDemo } = useDepartmentFlags();
	const { getNurseById } = useRosterLookup(chosenDepartment.department_id);
	const lastNameFirst = useLastNameFirst();
	const { data, isFetching } = useNurseRemoval({ departmentId: chosenDepartment.department_id, date: staffingDate });
	const listed = useMemo<NurseData[]>(() => {
		if (isHospitalDemo) return buildDemoListedShifts(week, dayNo, getNurseById, lastNameFirst);
		// DEFAULT: the list hasn't arrived yet — nothing is listed while it loads (the grid shows its skeleton).
		return data ?? [];
	}, [isHospitalDemo, week, dayNo, getNurseById, data, lastNameFirst]);
	return { listed, isFetching };
};

/** The listed staff the roster doesn't know — reported once per list, since their row can't be named. */
const useReportUnknownStaff = (listed: readonly NurseData[], getNurseById: (id: number) => Nurse | undefined) => {
	const { t } = useTranslation();
	const showErrorSnackbar = useErrorSnackbar();
	const unknownIds = useMemo(() => listed.filter((row) => !getNurseById(row.id)).map((row) => row.id), [listed, getNurseById]);
	const unknownKey = unknownIds.join(",");
	useEffect(() => {
		if (!unknownKey) return;
		showErrorSnackbar(t("remove.staff.error.unknown.staff"), { nurseIds: unknownKey });
	}, [unknownKey, showErrorSnackbar, t]);
};

/** Every job a shift can carry, plus the unassigned bucket — so every listed shift counts toward the date's windows. */
const useCountedJobs = (): Job[] => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	// DEFAULT: a department without a job catalog still counts every shift, under the unassigned bucket.
	return useMemo(() => [...(chosenDepartment.jobs ?? []), buildUnassignedJob(t("monthly.view.job.unassigned"))], [chosenDepartment.jobs, t]);
};

/** The date's windows and where each listed shift is placed. The list is scheduled shifts, so the week's scheduled shifts define them. */
const useWindows = (week: WeekData, dayNo: number, listed: readonly NurseData[]): RemoveStaffWindows => {
	const { chosenDepartment } = useChosenDepartment();
	const { timezone } = useDepartmentFlags();
	const countedJobs = useCountedJobs();
	return useMemo(
		() =>
			deriveRemoveStaffWindows({
				week,
				dayIndex: dayNo,
				showActual: false,
				shifts: collectPlacementShifts(listed, week, dayNo, timezone),
				visibleJobs: countedJobs,
				department: chosenDepartment,
			}),
		[week, dayNo, listed, timezone, countedJobs, chosenDepartment],
	);
};

export type RemoveStaffRowsModel = RemoveStaffWindows & {
	rows: RemoveStaffRow[];
	dateRange: DateTime[];
	staffingDate: DateTime;
	loading: boolean;
};

/**
 * The Remove Staff list for one day of the panel's schedule weeks (`weekIndex` and `dayNo` are positions in them): every shift on the date (day, evening and night alike) as one row
 * per SHIFT, the date's time windows and each shift's place in them, and the recent call-offs per nurse.
 */
export const useRemoveStaffRows = ({ weeks, weekIndex, dayNo }: { weeks: WeekData[]; weekIndex: number; dayNo: number }): RemoveStaffRowsModel => {
	const { chosenDepartment } = useChosenDepartment();
	const { isSnf, timezone } = useDepartmentFlags();
	const { getNurseById } = useRosterLookup(chosenDepartment.department_id);
	const locationsMap = useMemo(() => new Map(chosenDepartment.locations.map((location) => [location.id, location])), [chosenDepartment.locations]);
	const week = weeks[weekIndex];
	const staffingDate = staffingDateOf(week, dayNo);
	const { listed, isFetching } = useListedShifts(week, dayNo, staffingDate);
	useReportUnknownStaff(listed, getNurseById);
	const windows = useWindows(week, dayNo, listed);
	const lastNameFirst = useLastNameFirst();

	const nurseIds = useMemo(() => [...new Set(listed.map((row) => row.id))], [listed]);
	const periodStart = useMemo(() => staffingDate.minus({ weeks: CALL_OFF_LOOKBACK_WEEKS }).startOf("week"), [staffingDate]);
	const { data: callOffCounts } = useGetCalledOffShiftsThisWeek({ departmentId: chosenDepartment.department_id, periodStart, nurseIds });

	const timeOffOf = useNurseTimeOff();
	const buildWeekStrip = useCallback(
		(nurse: Nurse) =>
			generateShiftData({
				nurse,
				timeOff: timeOffOf(nurse.id),
				weekIndex,
				hospitalData: { weeks },
				targetDayIndex: dayNo,
				sourceDayIndex: dayNo,
				chosenDepartment,
				isSnf,
				locationsMap,
			}),
		[timeOffOf, weekIndex, weeks, dayNo, chosenDepartment, isSnf, locationsMap],
	);

	const rows = useMemo(
		() =>
			buildRemoveStaffRows(listed, {
				week,
				dayIndex: dayNo,
				placementByShiftId: windows.placementByShiftId,
				getNurseById,
				buildWeekStrip,
				jobNameOf: (jobId) => chosenDepartment.jobs?.find((job) => job.id === jobId)?.displayName,
				// DEFAULT: a nurse the call-off read hasn't returned (still loading, or none on record) shows zero.
				callOffsOf: (nurseId) => callOffCounts?.[nurseId] ?? NO_CALL_OFFS,
				timezone,
				lastNameFirst,
			}),
		[listed, week, dayNo, windows.placementByShiftId, getNurseById, buildWeekStrip, chosenDepartment.jobs, callOffCounts, timezone, lastNameFirst],
	);

	const counted = useMemo(() => countUnplacedAsOther(windows, rows), [windows, rows]);
	const dateRange = useMemo(() => createDateRange(staffingDate), [staffingDate]);
	return { ...counted, rows, dateRange, staffingDate, loading: isFetching };
};
